import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { supabase } from '@/services/supabase';
import { ROUTES } from '@/lib/constants';
import { resolveWCBlitzRedirect, SAM_ARMY_LANDING_PATH, SAMS_ARMY_LANDING_PATH } from '@/lib/wcblitz-routing';
import { AppLayout } from '@/components/layout/AppLayout';
import ArcadeToastContainer from '@/components/arcade/ArcadeToast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageLoader } from '@/components/ui/Loader';
import ConsentBanner from '@/components/privacy/ConsentBanner';

// Core pages — eagerly loaded for instant navigation
import { HomePage } from '@/pages/HomePage';
import DashboardPage from '@/pages/DashboardPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { BracketPage } from '@/pages/BracketPage';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { LeaguesPage } from '@/pages/LeaguesPage';
import { LeagueDetailPage } from '@/pages/LeagueDetailPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { CommunityPage } from '@/pages/CommunityPage';

// Lazy load less-critical pages
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const JoinPage = React.lazy(() => import('@/pages/JoinPage').then(m => ({ default: m.JoinPage })));
const ScoutPage = React.lazy(() => import('@/pages/ScoutPage').then(m => ({ default: m.ScoutPage })));
const StorePage = React.lazy(() => import('@/pages/StorePage').then(m => ({ default: m.StorePage })));
const BoardDetailPage = React.lazy(() => import('@/pages/BoardDetailPage').then(m => ({ default: m.BoardDetailPage })));
const ChallengesPage = React.lazy(() => import('@/pages/ChallengesPage').then(m => ({ default: m.ChallengesPage })));
const PremiumPage = React.lazy(() => import('@/pages/PremiumPage').then(m => ({ default: m.PremiumPage })));
const PoolsPage = React.lazy(() => import('@/pages/PoolsPage').then(m => ({ default: m.PoolsPage })));
const MorePage = React.lazy(() => import('@/pages/MorePage').then(m => ({ default: m.MorePage })));
const WorldCupSchedulePage = React.lazy(() => import('@/pages/WorldCupSchedulePage'));

const ComparePage = React.lazy(() => import('@/pages/ComparePage'));
const TvPage = React.lazy(() => import('@/pages/TvPage'));
const WallBracketPage = React.lazy(() => import('@/pages/WallBracketPage'));
const TeamPage = React.lazy(() => import('@/pages/TeamPage'));
const EdgesPage = React.lazy(() => import('@/pages/EdgesPage'));
const WCLayout = React.lazy(() => import('@/pages/wc/WCLayout'));
const MomentumPage = React.lazy(() => import('@/pages/MomentumPage'));
const PrivacyPage = React.lazy(() => import('@/pages/legal/LegalPages').then(m => ({ default: m.PrivacyPage })));
const TermsPage = React.lazy(() => import('@/pages/legal/LegalPages').then(m => ({ default: m.TermsPage })));
const OfficialRulesPage = React.lazy(() => import('@/pages/legal/LegalPages').then(m => ({ default: m.OfficialRulesPage })));
const PrivacyChoicesPage = React.lazy(() => import('@/pages/legal/LegalPages').then(m => ({ default: m.PrivacyChoicesPage })));
const AccessibilityStatementPage = React.lazy(() => import('@/pages/legal/LegalPages').then(m => ({ default: m.AccessibilityStatementPage })));
const ContactPage = React.lazy(() => import('@/pages/legal/LegalPages').then(m => ({ default: m.ContactPage })));
const HelpPage = React.lazy(() => import('@/pages/legal/LegalPages').then(m => ({ default: m.HelpPage })));
// Admin Pages
const AdminDashboard = React.lazy(() => import('@/pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const TournamentManagement = React.lazy(() => import('@/pages/admin/TournamentManagement').then(m => ({ default: m.TournamentManagement })));
const UserManagement = React.lazy(() => import('@/pages/admin/UserManagement').then(m => ({ default: m.UserManagement })));
const AnalyticsDashboard = React.lazy(() => import('@/pages/admin/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const FeatureFlags = React.lazy(() => import('@/pages/admin/FeatureFlags').then(m => ({ default: m.FeatureFlags })));
const SimulationPanel = React.lazy(() => import('@/pages/admin/SimulationPanel').then(m => ({ default: m.SimulationPanel })));
const BracketsAdmin = React.lazy(() => import('@/pages/admin/BracketsAdmin').then(m => ({ default: m.BracketsAdmin })));
const ChallengesAdmin = React.lazy(() => import('@/pages/admin/ChallengesAdmin').then(m => ({ default: m.ChallengesAdmin })));
const CommunityAdmin = React.lazy(() => import('@/pages/admin/CommunityAdmin').then(m => ({ default: m.CommunityAdmin })));

// 404 Page
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

// Loading fallback component
const LoadingFallback: React.FC<{ text?: string }> = ({ text = "Loading..." }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 200);
    return () => clearTimeout(timer);
  }, []);
  if (!show) return <div className="min-h-screen bg-dark-700" />;
  return (
    <div className="min-h-screen bg-dark-700 flex items-center justify-center">
      <PageLoader text={text} />
    </div>
  );
};

// Route protection component with timeout safety
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, setLoading } = useAuthStore();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Safety timeout - don't let auth loading hang forever
  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => {
      console.warn('Auth loading timed out after 5s');
      setLoading(false);
      setTimedOut(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isLoading, setLoading]);

  if (isLoading && !timedOut) {
    return <LoadingFallback text="Checking authentication..." />;
  }

  if (!user) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${ROUTES.LOGIN}?redirect=${redirect}`} replace />;
  }

  return <>{children}</>;
};

// Admin route protection
// Gates by user_profiles.role OR by successful /sim/status response (SIM_ADMIN_IDS allowlist)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  const [simAdminChecked, setSimAdminChecked] = React.useState(false);
  const [isSimAdmin, setIsSimAdmin] = React.useState(false);

  React.useEffect(() => {
    if (user && user.role !== 'admin') {
      // Check if user is a sim admin (server-side allowlist)
      import('@/services/api').then(({ simApi }) => {
        simApi.status()
          .then(() => { setIsSimAdmin(true); setSimAdminChecked(true); })
          .catch(() => { setIsSimAdmin(false); setSimAdminChecked(true); });
      });
    } else {
      setSimAdminChecked(true);
    }
  }, [user]);

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!simAdminChecked) {
    return null; // loading
  }

  if (user?.role !== 'admin' && !isSimAdmin) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

// Public route component (redirects to bracket if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuthStore();
  const location = useLocation();

  // Don't block rendering for public routes — show the page immediately
  // Only redirect if we KNOW user is authenticated (not while loading)
  if (!isLoading && user) {
    const searchParams = new URLSearchParams(location.search);
    const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
    const isWCBlitzHost = hostname === 'wcblitz.com' || hostname.endsWith('.wcblitz.com');
    const redirectTo = searchParams.get('redirect') || (isWCBlitzHost ? '/wc/knockout' : '/');
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

// Auth pages wrapper (doesn't use AppLayout)
const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-700 to-dark-600">
      {children}
    </div>
  );
};

const HomeOrDashboard: React.FC = () => {
  const { user } = useAuthStore()
  return user ? (
    <AppLayout>
      <DashboardPage />
    </AppLayout>
  ) : <HomePage />
}


const LegacyBracketRouter: React.FC = () => {
  const { bracketId } = useParams<{ bracketId: string }>();
  return <Navigate to={bracketId ? `/bracket/${bracketId}` : '/bracket'} replace />;
};

const WCBlitzDomainGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const redirectTo =
    typeof window === 'undefined'
      ? null
      : resolveWCBlitzRedirect(window.location.hostname, location.pathname);

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { setUser, setSession, setLoading, setReady } = useAuthStore();

  // Helper: hydrate auth state from a Supabase session
  const hydrateSession = (session: any) => {
    if (session?.user) {
      setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at || 0,
        user: {
          id: session.user.id,
          email: session.user.email || '',
          display_name: session.user.user_metadata?.display_name,
          avatar_url: session.user.user_metadata?.avatar_url,
          role: 'user' as const,
          created_at: session.user.created_at,
          last_sign_in_at: session.user.last_sign_in_at,
        },
      });
      setUser({
        id: session.user.id,
        email: session.user.email || '',
        display_name: session.user.user_metadata?.display_name,
        avatar_url: session.user.user_metadata?.avatar_url,
        role: 'user' as const,
        created_at: session.user.created_at,
        last_sign_in_at: session.user.last_sign_in_at,
      });
    } else {
      setUser(null);
      setSession(null);
    }
  };

  useEffect(() => {
    // Handle auth errors from URL hash (e.g., expired confirmation links)
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const errorCode = params.get('error_code');
      const errorDescription = params.get('error_description');
      
      if (errorCode === 'otp_expired') {
        toast.error('Your confirmation link has expired. Please sign up again or request a new link.', { duration: 8000 });
        window.history.replaceState(null, '', window.location.pathname);
      } else if (errorCode === 'access_denied') {
        toast.error(errorDescription?.replace(/\+/g, ' ') || 'Access denied. Please try again.', { duration: 6000 });
        window.history.replaceState(null, '', window.location.pathname);
      } else if (errorCode) {
        toast.error(errorDescription?.replace(/\+/g, ' ') || 'Something went wrong. Please try again.', { duration: 6000 });
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    // 1. Hydrate from getSession() — fast, reads from localStorage
    supabase.auth.getSession().then(({ data: { session } }) => {
      hydrateSession(session);
      setReady();
      setLoading(false);
    }).catch(() => {
      setReady();
      setLoading(false);
    });

    // 2. Listen for ongoing auth changes (token refresh, sign in/out)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          hydrateSession(session);
          // Auto-join league if pending invite code was saved before auth
          if (event === 'SIGNED_IN' && session?.user) {
            const pendingCode = sessionStorage.getItem('bb_pending_join_code');
            if (pendingCode) {
              sessionStorage.removeItem('bb_pending_join_code');
              try {
                const res = await (await import('@/services/api')).leaguesApi.joinByCode({ inviteCode: pendingCode });
                const leagueId = res?.league?.id || res?.leagueId;
                toast.success(res?.league?.name ? `Joined ${res.league.name}!` : 'Joined league!');
                if (leagueId) {
                  setTimeout(() => { window.location.href = `/leagues/${leagueId}`; }, 500);
                }
              } catch (e: any) {
                const msg = e?.message || '';
                if (msg.toLowerCase().includes('already')) {
                  toast.success('Already a member!');
                } else {
                  toast.error(msg || 'Failed to join league from invite');
                }
              }
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setSession, setLoading, setReady]);

  // Fetch teams data (non-blocking, public endpoint — no auth needed)
  useEffect(() => {
    useTeamsStore.getState().fetchTeams();
  }, []);

  return (
    <Router>
      <WCBlitzDomainGate>
        <div className="min-h-screen text-white">
        <Routes>
          {/* Public routes (auth pages) */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <PublicRoute>
                <AuthWrapper>
                    <LoginPage />
                </AuthWrapper>
              </PublicRoute>
            }
          />
          <Route
            path={ROUTES.REGISTER}
            element={
              <AuthWrapper>
                  <RegisterPage />
              </AuthWrapper>
            }
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={
              <PublicRoute>
                <AuthWrapper>
                  <Suspense fallback={<LoadingFallback text="Loading password reset..." />}>
                    <ForgotPasswordPage />
                  </Suspense>
                </AuthWrapper>
              </PublicRoute>
            }
          />

          {/* Home: Dashboard for authenticated, marketing for visitors */}
          <Route
            path={ROUTES.HOME}
            element={
                <HomeOrDashboard />
            }
          />
          <Route
            path={ROUTES.WORLD_CUP_SCHEDULE}
            element={
              <Suspense fallback={<LoadingFallback text="Loading World Cup schedule..." />}>
                <WorldCupSchedulePage />
              </Suspense>
            }
          />

          <Route path="/privacy" element={<Suspense fallback={<LoadingFallback text="Loading privacy policy..." />}><PrivacyPage /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<LoadingFallback text="Loading terms..." />}><TermsPage /></Suspense>} />
          <Route path="/official-rules" element={<Suspense fallback={<LoadingFallback text="Loading official rules..." />}><OfficialRulesPage /></Suspense>} />
          <Route path="/privacy-choices" element={<Suspense fallback={<LoadingFallback text="Loading privacy choices..." />}><PrivacyChoicesPage /></Suspense>} />
          <Route path="/accessibility" element={<Suspense fallback={<LoadingFallback text="Loading accessibility statement..." />}><AccessibilityStatementPage /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<LoadingFallback text="Loading contact..." />}><ContactPage /></Suspense>} />
          <Route path="/help" element={<Suspense fallback={<LoadingFallback text="Loading help..." />}><HelpPage /></Suspense>} />
          <Route path="/sam" element={<Navigate to={SAM_ARMY_LANDING_PATH} replace />} />
          <Route path="/sams-army" element={<Navigate to={SAMS_ARMY_LANDING_PATH} replace />} />

          {/* Join deep link — works logged in or not */}
          <Route
            path="/join/:token"
            element={
              <Suspense fallback={<LoadingFallback text="Joining league..." />}>
                <JoinPage />
              </Suspense>
            }
          />
          <Route
            path="/join"
            element={
              <Suspense fallback={<LoadingFallback text="Joining league..." />}>
                <JoinPage />
              </Suspense>
            }
          />
          {/* Legacy path support */}
          <Route
            path="/leagues/join"
            element={
              <Suspense fallback={<LoadingFallback text="Joining league..." />}>
                <JoinPage />
              </Suspense>
            }
          />

          {/* Protected routes (require authentication) */}

          <Route
            path="/bracket/:bracketId"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <BracketPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bracket/:bracketId/*"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <BracketPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brackets/:bracketId"
            element={<LegacyBracketRouter />}
          />
          <Route
            path="/brackets/:bracketId/*"
            element={<LegacyBracketRouter />}
          />
          <Route
            path={ROUTES.BRACKET}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <BracketPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bracket/:bracketId"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <BracketPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LEADERBOARD}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <LeaderboardPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LEAGUES}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <LeaguesPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leagues/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <LeagueDetailPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <ProfilePage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SCOUT}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback text="Loading scout..." />}>
                    <ScoutPage />
                  </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <CommunityPage />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/:slug"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback text="Loading board..." />}>
                    <BoardDetailPage />
                  </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.STORE}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback text="Loading store..." />}>
                    <StorePage />
                  </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CHALLENGES}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback text="Loading challenges..." />}>
                    <ChallengesPage />
                  </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PREMIUM}
            element={
              <AppLayout>
                <ErrorBoundary>
                <Suspense fallback={<LoadingFallback text="Loading premium..." />}>
                  <PremiumPage />
                </Suspense>
                </ErrorBoundary>
              </AppLayout>
            }
          />
          <Route
            path={ROUTES.POOLS}
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback text="Loading pools..." />}>
                    <PoolsPage />
                  </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />

            <Route path="/compare" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" /></div>}><ComparePage /></Suspense>} />
            <Route path="/tv" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" /></div>}><TvPage /></Suspense>} />
            <Route path="/team/:slug" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" /></div>}><TeamPage /></Suspense>} />
            <Route path="/hot" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" /></div>}><MomentumPage /></Suspense>} />
            <Route path="/wc/*" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" /></div>}><WCLayout /></Suspense>} />
            <Route path="/edges" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" /></div>}><EdgesPage /></Suspense>} />
            <Route path="/wall" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" /></div>}><WallBracketPage /></Suspense>} />
          <Route
            path="/more"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback text="Loading more..." />}>
                    <MorePage />
                  </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin routes with nested structure */}
          <Route path="/admin/*" element={
            <AdminRoute>
              <Routes>
                <Route 
                  index 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading admin panel..." />}>
                      <AdminDashboard />
                    </Suspense>
                  } 
                />
                <Route 
                  path="tournament" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading tournament management..." />}>
                      <TournamentManagement />
                    </Suspense>
                  } 
                />
                <Route 
                  path="users" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading user management..." />}>
                      <UserManagement />
                    </Suspense>
                  } 
                />
                <Route 
                  path="analytics" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading analytics..." />}>
                      <AnalyticsDashboard />
                    </Suspense>
                  } 
                />
                <Route 
                  path="brackets" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading brackets..." />}>
                      <BracketsAdmin />
                    </Suspense>
                  } 
                />
                <Route 
                  path="challenges" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading challenges..." />}>
                      <ChallengesAdmin />
                    </Suspense>
                  } 
                />
                <Route 
                  path="community" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading community admin..." />}>
                      <CommunityAdmin />
                    </Suspense>
                  } 
                />
                <Route 
                  path="flags" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading feature flags..." />}>
                      <FeatureFlags />
                    </Suspense>
                  } 
                />
                <Route 
                  path="simulation" 
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading simulation panel..." />}>
                      <SimulationPanel />
                    </Suspense>
                  } 
                />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </AdminRoute>
          } />

          {/* Catch all route - 404 page */}
          <Route 
            path="*" 
            element={
              <Suspense fallback={<LoadingFallback text="Loading..." />}>
                <NotFoundPage />
              </Suspense>
            } 
          />
        </Routes>

        {/* Global toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#252547',
              color: '#ffffff',
              border: '1px solid #4A5568',
            },
            success: {
              style: {
                border: '1px solid #00D084',
              },
            },
            error: {
              style: {
                border: '1px solid #E53E3E',
              },
            },
          }}
        />
        <ArcadeToastContainer />
        <ConsentBanner />
        </div>
      </WCBlitzDomainGate>
    </Router>
  );
};

export default App;
